import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import {
  AppBar,
  Box,
  Breadcrumbs,
  IconButton,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useScrollTrigger,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { BrandLogo } from '@/components/EntityMenu/BrandLogo';
import { layout } from '@/constants/layout';
import { useAppShell } from '@/shell';
import type { EntityOption } from '@/types/entity';
import { EntitySelector } from './EntitySelector';
import { EnvironmentBadge } from './EnvironmentBadge';
import { HeaderActions } from './HeaderActions';

export interface AppHeaderProps {
  entities?: EntityOption[];
  selectedEntityId?: string;
  onEntityChange?: (entity: EntityOption) => void;
  onLogoClick?: () => void;
  entitySelectorDisabled?: boolean;
  entitiesLoading?: boolean;
  scrollContainerRef?: RefObject<HTMLElement | null>;
  breadcrumbSegments?: string[];
  onPopOut?: () => void;
}

export function AppHeader({
  entities: entitiesProp,
  selectedEntityId: selectedEntityIdProp,
  onEntityChange,
  onLogoClick,
  entitySelectorDisabled = false,
  entitiesLoading = false,
  scrollContainerRef,
  breadcrumbSegments,
  onPopOut,
}: AppHeaderProps) {
  const { t } = useTranslation('entity-portal');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const { currentEntityId, setCurrentEntityId } = useAppShell();

  const entities = useMemo(() => entitiesProp ?? [], [entitiesProp]);

  const [internalEntityId, setInternalEntityId] = useState(
    () => selectedEntityIdProp ?? currentEntityId ?? entities[0]?.id ?? '',
  );

  const selectedEntityId = selectedEntityIdProp ?? internalEntityId;
  const [scrollTarget, setScrollTarget] = useState<HTMLElement | null>(null);

  const scrollTrigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 4,
    target: scrollTarget ?? undefined,
  });

  const entityMap = useMemo(
    () => new Map(entities.map((entity) => [entity.id, entity])),
    [entities],
  );

  useEffect(() => {
    setScrollTarget(scrollContainerRef?.current ?? null);
  }, [scrollContainerRef]);

  useEffect(() => {
    if (selectedEntityIdProp !== undefined) {
      return;
    }

    const entityStillExists = entities.some(
      (entity) => entity.id === internalEntityId,
    );

    if (!internalEntityId || !entityStillExists) {
      const fallbackEntityId = entities[0]?.id ?? '';
      setInternalEntityId(fallbackEntityId);
      setCurrentEntityId(fallbackEntityId || null);
    }
  }, [entities, internalEntityId, selectedEntityIdProp, setCurrentEntityId]);

  const handleEntitySelect = useCallback(
    (entityId: string) => {
      if (selectedEntityIdProp === undefined) {
        setInternalEntityId(entityId);
      }

      setCurrentEntityId(entityId);

      const entity = entityMap.get(entityId);
      if (entity) {
        onEntityChange?.(entity);
      }
    },
    [entityMap, onEntityChange, selectedEntityIdProp, setCurrentEntityId],
  );

  const handleLogoClick = useCallback(() => {
    onLogoClick?.();
  }, [onLogoClick]);

  return (
    <AppBar
      position="sticky"
      color="inherit"
      elevation={scrollTrigger ? 2 : 0}
      sx={{
        zIndex: theme.zIndex.drawer + 1,
        borderBottom: 1,
        borderColor: scrollTrigger ? 'transparent' : 'divider',
        boxShadow: scrollTrigger ? undefined : 'none',
        bgcolor: 'background.paper',
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          px: { xs: 1.5, sm: 2, md: 2.5 },
          gap: { xs: 1, sm: 1.5 },
          minHeight: `${layout.headerHeight}px !important`,
          height: layout.headerHeight,
          display: 'grid',
          gridTemplateColumns: {
            xs: 'auto 1fr auto',
            md: 'auto minmax(0, 1fr) auto',
          },
          alignItems: 'center',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={{ xs: 0.75, sm: 1 }}
          sx={{ flexShrink: 0, minWidth: 0 }}
        >
          <BrandLogo variant="icon" onClick={handleLogoClick} />
          <EntitySelector
            entities={entities}
            selectedEntityId={selectedEntityId}
            onChange={handleEntitySelect}
            compact={isMobile || isTablet}
            disabled={entitySelectorDisabled || entities.length === 0}
            loading={entitiesLoading}
          />
          {breadcrumbSegments?.length ? (
            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="small" />}
              aria-label={t('appHeader.appHeaderBreadcrumbAria')}
              sx={{
                display: { xs: 'none', sm: 'flex' },
                minWidth: 0,
                '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' },
                '& .MuiBreadcrumbs-separator': { mx: 0.1 },
              }}
            >
              {/* Leading empty crumb forces the separator to render before the first visible segment. */}
              <Box key="breadcrumb-root" sx={{ width: 0 }} />
              {breadcrumbSegments.map((segment, index) => (
                <Typography
                  key={`${segment}-${index}`}
                  variant="subtitle1"
                  color="text.secondary"
                  noWrap
                >
                  {segment}
                </Typography>
              ))}
            </Breadcrumbs>
          ) : null}
          {onPopOut ? (
            <Tooltip title={t('appHeader.appHeaderPopOutLabel')}>
              <IconButton
                size="small"
                aria-label={t('appHeader.appHeaderPopOutLabel')}
                onClick={onPopOut}
                sx={{
                  flexShrink: 0,
                  color: 'text.secondary',
                  transition: (theme) =>
                    theme.transitions.create(['color', 'background-color']),
                  '&:hover': {
                    color: 'primary.main',
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <OpenInNewOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>

        <Box sx={{ minWidth: 0 }} />

        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{ justifySelf: 'end' }}
        >
          <EnvironmentBadge />
          <HeaderActions />
        </Stack>
      </Toolbar>
    </AppBar>
  );
}
